import { useCallback, useContext } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import axios from "axios";
import { AccountContext } from "auth/accountProvider";

// Base URL of the consumer-data-processor API (same var the media/CPI queries use).
const API_BASE = process.env.REACT_APP_LPI_API_URL;

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

// Resolves the session tokens the super_admin /admin endpoints expect. Mirrors
// the helper in mediaQueries (which itself mirrors overviewData's), kept local
// for the same reason: neither module owns the other's private hook.
const useSessionTokens = (): (() => Promise<SessionTokens>) => {
  const { getSession } = useContext(AccountContext);
  return useCallback(async (): Promise<SessionTokens> => {
    const session = await getSession();
    if (!session) throw new Error("Session expired. Please log in again.");
    const jwtToken = session.getAccessToken?.()?.getJwtToken?.();
    const sessionId = session.getIdToken?.()?.payload?.sessionId;
    if (!jwtToken || !sessionId) {
      throw new Error("Session tokens missing. Please log in again.");
    }
    return { jwtToken, sessionId };
  }, [getSession]);
};

const authHeaders = (t: SessionTokens): Record<string, string> => ({
  Authorization: `Session ${t.jwtToken}`,
  "X-Session-Id": t.sessionId,
});

/** One tenant, as returned by GET /admin/organizations (already sorted by name). */
export interface AdminOrganization {
  id: string;
  name: string;
}

/**
 * One audit row from GET /admin/deletions. Field names mirror the backend's
 * DeletionAuditRecord exactly; dates arrive as ISO strings over JSON.
 */
export interface DeletionAuditRow {
  deletionAuditId: string;
  organizationId: string;
  filename: string;
  fileType: string;
  deletedByUserId: string | null;
  deletedByEmail: string | null;
  deletedAt: string;
  totalRowsDeleted: number;
  tablesDeleted: unknown;
  scopeStart: string | null;
  scopeEnd: string | null;
  scopeTenantWide: boolean;
  recomputeQueued: boolean | null;
}

/** Envelope of GET /admin/deletions. */
export interface DeletionsPage {
  deletions: DeletionAuditRow[];
  total: number;
  limit: number;
  offset: number;
  count: number;
  maxLimit: number;
  defaultLimit: number;
}

/** Filters/paging accepted by GET /admin/deletions. Dates are ISO-8601. */
export interface DeletionsQuery {
  organizationId?: string;
  from?: string;
  to?: string;
  limit: number;
  offset: number;
}

/**
 * Page size for the deletions table. The endpoint defaults to 50 and clamps
 * anything above 200; this stays inside both.
 */
export const DELETIONS_PAGE_SIZE = 50;

/**
 * The tenant list backing the org filter. Fails loudly (the query errors) —
 * the deletions view treats an absent list as "no names known" and falls back
 * to raw org ids rather than breaking the page.
 */
export const useAdminOrganizations = (): UseQueryResult<AdminOrganization[]> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["admin", "organizations"] as const,
    queryFn: async ({ signal }): Promise<AdminOrganization[]> => {
      const auth = await getAuth();
      const res = await axios.get<{ organizations: AdminOrganization[] }>(
        `${API_BASE}/admin/organizations`,
        { headers: authHeaders(auth), signal }
      );
      return res.data.organizations ?? [];
    },
  });
};

/**
 * One page of the per-file deletion audit trail, newest first.
 *
 * The full query (filters + offset) is part of the query key, so changing any
 * filter or turning a page is a new cache entry and therefore a new fetch —
 * which is what makes this work under the app-wide `staleTime: Infinity`.
 */
export const useDeletions = (
  query: DeletionsQuery
): UseQueryResult<DeletionsPage> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["admin", "deletions", query] as const,
    keepPreviousData: true,
    queryFn: async ({ signal }): Promise<DeletionsPage> => {
      const auth = await getAuth();
      const params: Record<string, string> = {
        limit: String(query.limit),
        offset: String(query.offset),
      };
      if (query.organizationId) params.organizationId = query.organizationId;
      if (query.from) params.from = query.from;
      if (query.to) params.to = query.to;

      const res = await axios.get<DeletionsPage>(`${API_BASE}/admin/deletions`, {
        headers: authHeaders(auth),
        params,
        signal,
      });
      return res.data;
    },
  });
};

// --- Tenant removal (super_admin) --------------------------------------------

export type TenantRemovalStatus = "SCHEDULED" | "PURGING" | "PURGED" | "RESTORED" | "FAILED";

/**
 * One removal row. BigInt columns arrive as STRINGS — the backend serialises
 * them that way because JSON has no BigInt, exactly as DeletionAudit does.
 */
export interface TenantRemovalRow {
  tenantRemovalId: string;
  organizationId: string;
  organizationName: string;
  status: TenantRemovalStatus;
  requestedByEmail: string | null;
  requestedAt: string;
  purgeAfter: string;
  purgeStartedAt: string | null;
  purgeFinishedAt: string | null;
  restoredAt: string | null;
  restoredByEmail: string | null;
  totalRowsDeleted: string;
  previewTotalRows: string;
  lastError: string | null;
}

/** What GET /admin/tenant/:id/preview returns. */
export interface TenantPreview {
  organizationId: string;
  tables: { table: string; rowCount: number }[];
  totalRows: number;
}

/**
 * Statuses that are still moving. The app sets `staleTime: Infinity` globally,
 * so without an explicit refetchInterval a purge in progress would never update.
 */
export const POLLED_STATUSES: TenantRemovalStatus[] = ["SCHEDULED", "PURGING"];

const REMOVALS_POLL_MS = 5000;

/**
 * The `refetchInterval` predicate itself, exported so it can be unit-tested
 * directly against every status (including the pre-first-fetch `undefined`)
 * rather than only indirectly through `POLLED_STATUSES` membership.
 */
export const removalsRefetchInterval = (
  data: TenantRemovalRow[] | undefined
): number | false =>
  Array.isArray(data) && data.some((r) => POLLED_STATUSES.includes(r.status)) ? REMOVALS_POLL_MS : false;

export const useRemovals = (): UseQueryResult<TenantRemovalRow[]> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["admin", "removals"] as const,
    refetchInterval: removalsRefetchInterval,
    queryFn: async ({ signal }): Promise<TenantRemovalRow[]> => {
      const auth = await getAuth();
      const res = await axios.get<{ removals: TenantRemovalRow[] }>(`${API_BASE}/admin/removals`, {
        headers: authHeaders(auth),
        signal,
      });
      return res.data.removals ?? [];
    },
  });
};

/** Row counts for the confirmation screen. Disabled until a tenant is picked. */
export const useTenantPreview = (organizationId: string | null): UseQueryResult<TenantPreview> => {
  const getAuth = useSessionTokens();
  return useQuery({
    queryKey: ["admin", "tenantPreview", organizationId] as const,
    enabled: Boolean(organizationId),
    queryFn: async ({ signal }): Promise<TenantPreview> => {
      const auth = await getAuth();
      const res = await axios.get<TenantPreview>(
        `${API_BASE}/admin/tenant/${organizationId}/preview`,
        { headers: authHeaders(auth), signal }
      );
      return res.data;
    },
  });
};

/** Invalidate everything a removal changes: the list AND the tenant dropdown. */
const useRemovalInvalidation = (): (() => Promise<void>) => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "removals"] });
    await queryClient.invalidateQueries({ queryKey: ["admin", "organizations"] });
  };
};

export const useCreateRemoval = (): UseMutationResult<
  TenantRemovalRow,
  unknown,
  { organizationId: string; confirmName: string }
> => {
  const getAuth = useSessionTokens();
  const invalidate = useRemovalInvalidation();
  return useMutation({
    mutationFn: async ({ organizationId, confirmName }) => {
      const auth = await getAuth();
      const res = await axios.post<{ removal: TenantRemovalRow }>(
        `${API_BASE}/admin/tenant/${organizationId}/removal`,
        { confirmName },
        { headers: authHeaders(auth) }
      );
      return res.data.removal;
    },
    onSuccess: invalidate,
  });
};

export const useRestoreRemoval = (): UseMutationResult<TenantRemovalRow, unknown, string> => {
  const getAuth = useSessionTokens();
  const invalidate = useRemovalInvalidation();
  return useMutation({
    mutationFn: async (tenantRemovalId) => {
      const auth = await getAuth();
      const res = await axios.post<{ removal: TenantRemovalRow }>(
        `${API_BASE}/admin/removals/${tenantRemovalId}/restore`,
        {},
        { headers: authHeaders(auth) }
      );
      return res.data.removal;
    },
    onSuccess: invalidate,
  });
};

export const useRetryRemoval = (): UseMutationResult<TenantRemovalRow, unknown, string> => {
  const getAuth = useSessionTokens();
  const invalidate = useRemovalInvalidation();
  return useMutation({
    mutationFn: async (tenantRemovalId) => {
      const auth = await getAuth();
      const res = await axios.post<{ removal: TenantRemovalRow }>(
        `${API_BASE}/admin/removals/${tenantRemovalId}/retry`,
        {},
        { headers: authHeaders(auth) }
      );
      return res.data.removal;
    },
    onSuccess: invalidate,
  });
};
